import { BILLING_LIMITS, type BillingLimit } from '@aysar/contracts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import {
  UNKNOWN_ERROR_CODE,
  type ApiError,
} from '@/shared/utils/transform-error/transform-error.types';

import { useTierLimits } from '../../hooks/useTierLimits/useTierLimits';
import {
  type TierLimitDraft,
  draftOfRow,
  rowOfDraft,
  tierLimitFields,
  tierLimitSchema,
} from '../../utils/tier-limit-fields/tier-limit-fields.util';

export interface UseTierLimitEditorModalLogicInput {
  /** The tier being edited — a serializable modal payload. */
  tier: string;
  onDone: () => void;
}

export interface UseTierLimitEditorModalLogic {
  title: string;
  description: string;
  saveLabel: string;
  /** Set when the base currently differs by gender and one number will replace it. */
  genderNotice: string | null;
  fields: readonly FieldDef[];
  schema: ReturnType<typeof tierLimitSchema>;
  initialValues: TierLimitDraft;
  /** Held until the limits arrive — RHF reads `defaultValues` exactly once. */
  isLoading: boolean;
  submit: (values: TierLimitDraft) => Promise<void>;
}

/**
 * One tier's numeric levers (U5: the modal below is presentation only).
 *
 * ## A form rather than a confirmation, even though the key is dangerous
 *
 * `useConfirm` compares a typed phrase against a constant and throws it away; it
 * cannot carry a value. This act needs numbers only a person can type, so it is
 * a `DynamicFormModal` and **the danger lives in the copy** — the description
 * says the save lands immediately for every paying member on the tier. That is
 * P25d2's rule in the other direction: "is this destructive?" does not decide
 * the vehicle, "does the server need something only a person can type?" does.
 *
 * ## The boxes hold the OVERRIDE and the sentences hold the resolution
 *
 * An inheriting lever's own value is *nothing*, so its box is empty and the
 * number it inherits goes in the sentence beneath. And that number is
 * **per gender** while this override is global-only, so the sentence names every
 * scope and a notice says what setting one costs.
 *
 * It reads `billing.tierLimits` itself rather than taking it as modal data, for
 * `useDirectPriceEditorModalLogic`'s reason: the payload stays a serializable
 * key, and an open dialog keeps agreeing with the server while the board
 * refetches underneath it.
 */
export function useTierLimitEditorModalLogic({
  tier,
  onDone,
}: UseTierLimitEditorModalLogicInput): UseTierLimitEditorModalLogic {
  const { t } = useTranslation('subscriptions');
  const toast = useToast();
  const limits = useTierLimits();

  const { map, baseFor, isBaseGenderScoped, mayManage, saveRow, state } = limits;

  const initialValues = useMemo(() => draftOfRow(map[tier] ?? {}), [map, tier]);

  const describe = useCallback(
    (lever: BillingLimit) =>
      t('tierLimits.inheritedNow', {
        values: baseFor(lever)
          .map(
            (base) =>
              `${t(`pricing.scope.${base.scope}`)} ${base.value ?? t('tierLimits.unresolved')}`,
          )
          .join(' · '),
      }),
    [t, baseFor],
  );

  const fields = useMemo(
    () => tierLimitFields({ t, describe, isDisabled: !mayManage }),
    [t, describe, mayManage],
  );

  const submit = useCallback(
    async (values: TierLimitDraft) => {
      if (!mayManage) {
        // Shaped as an `ApiError` rather than thrown bare: the form renders a
        // thrown error's own message as its failure summary, and an internal
        // token would then reach the screen untranslated (U8). `status: 0` is
        // the literal truth — the request never left the browser.
        const refusal: ApiError = {
          status: 0,
          code: 'forbidden',
          message: t('tierLimits.readOnly'),
        };

        throw refusal;
      }

      try {
        await saveRow(tier, rowOfDraft(values));
      } catch (error) {
        // A third tier under the api's own words. Keyed off the CODE, because
        // `toApiError` never returns an empty message — it substitutes one
        // generic sentence — so a check on the message could never fire, and
        // this surface can say more than "something went wrong" (P25d2).
        const refused = toApiError(error);

        throw {
          ...refused,
          message:
            refused.code === UNKNOWN_ERROR_CODE ? t('tierLimits.editor.failed') : refused.message,
        } satisfies ApiError;
      }

      toast.success(t('tierLimits.editor.saved'));
      onDone();
    },
    [mayManage, saveRow, tier, toast, t, onDone],
  );

  return {
    title: t('tierLimits.editor.title', { tier }),
    description: t('tierLimits.editor.description'),
    saveLabel: t('tierLimits.editor.save'),
    // Only when it is true today: a standing warning about a per-gender base on
    // a deployment that does not have one is noise the operator learns to skip.
    genderNotice: BILLING_LIMITS.some((lever) => isBaseGenderScoped(lever))
      ? t('tierLimits.genderNotice')
      : null,
    fields,
    schema: tierLimitSchema(),
    initialValues,
    isLoading: state === 'loading',
    submit,
  };
}
